import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MdExpandLess, MdExpandMore, MdViewColumn, MdVisibilityOff } from 'react-icons/md';
import { RxCaretDown, RxCaretSort } from 'react-icons/rx';

const BaseTable = ({
  rows = [],
  columns = [],
  dropdownMenu = () => <div>No Menu</div>,
  onRowClick,
  selectedRowId = null,
  selectedRowIds = [],
  expandable = false,
  altRowSettings = {},
  customStyles = {},
  muiTableContainerProps = {},
  muiTablePaperProps = {},
  enableRowVirtualization = true,
  initialState = {},
}) => {
  return (
    <MaterialReactTable
      enableColumnFilters={false}
      initialState={{
        showColumnFilters: false,
        ...initialState,
      }}
      columnFilterDisplayMode="popover"
      enableColumnActions={true}
      enableHiding={true}
      renderColumnActionsMenuItems={({ column, table }) => [
        <MenuItem key="hide" onClick={() => column.toggleVisibility(false)} sx={{ m: 0 }}>
          <ListItemIcon>
            <MdVisibilityOff />
          </ListItemIcon>
          <ListItemText>Hide</ListItemText>
        </MenuItem>,
        <MenuItem
          key="show-all"
          onClick={() => {
            table.getAllColumns().forEach((col) => {
              if (col.getCanHide()) {
                col.toggleVisibility(true);
              }
            });
          }}
          sx={{ m: 0 }}
        >
          <ListItemIcon>
            <MdViewColumn />
          </ListItemIcon>
          <ListItemText>Show All Columns</ListItemText>
        </MenuItem>,
      ]}
      icons={{
        SortIcon: () => <RxCaretSort />,
        ArrowDownwardIcon: () => <RxCaretDown />,
      }}
      columns={columns}
      data={rows}
      enableBottomToolbar={false}
      enableTopToolbar={false}
      enableExpanding={expandable}
      enableExpandAll={false}
      enableRowVirtualization={enableRowVirtualization}
      enablePagination={false}
      rowVirtualizerOptions={{ estimateSize: () => 70 }}
      muiExpandButtonProps={({ row }) => ({
        children: row.getIsExpanded() ? <MdExpandLess /> : <MdExpandMore />,
        sx: { display: 'none' }, // Hide since we're handling expand via row click
      })}
      // Table Container styles
      muiTablePaperProps={{
        ...muiTablePaperProps,
        sx: {
          width: '100%',
          backgroundColor: '#222A2D',
          boxShadow: 'none',
          borderRadius: 0,
          ...customStyles.container,
          ...(muiTablePaperProps.sx || {}),
        },
      }}
      // Allow callers to pass container props (controls the internal scroll area)
      muiTableContainerProps={{
        sx: {
          overflowX: 'auto',
          overflowY: 'visible',
          ...(muiTableContainerProps.sx || {}),
        },
        ...muiTableContainerProps,
      }}
      // Hides the default expand column
      displayColumnDefOptions={{
        'mrt-row-expand': {
          muiTableHeadCellProps: { sx: { display: 'none' } },
          muiTableBodyCellProps: { sx: { display: 'none' } },
        },
      }}
      // Header styles
      muiTableHeadProps={{
        sx: {
          opacity: 1, // Makes sure the header is not semi transparent
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          backgroundColor: '#222A2D',
          borderBottom: '1px solid #56577A',
          color: 'white',
          '& .MuiButtonBase-root': { opacity: 1, color: 'white' }, // Targets the vertical ellipsis button
          '& .MuiSvgIcon-root': { fill: 'white' }, // Targets the sort button
          padding: '8px',
          ...customStyles.header,
        },
      }}
      // Cell styles
      muiTableBodyCellProps={({ cell }) => ({
        sx: {
          ...customStyles.cell,
          '&:last-of-type': {
            borderRight: 'none',
          },
        },
      })}
      // Row styles
      muiTableBodyRowProps={({ row }) => {
        const disabled = !!row?.original?.disabled; //If disabled, change cursor and prevent click actions
        return {
          onClick: () => {
            if (disabled) return; // Early return to prevent any click actions if the row is disabled
            expandable && row.toggleExpanded();
            onRowClick && onRowClick(row.original);
          },
          hover: false,
          sx: {
            backgroundColor: '#222A2D',
            ...(altRowSettings?.alternate && {
              backgroundColor: row.index % 2 ? altRowSettings?.color1 : altRowSettings?.color2,
            }),
            ...customStyles.row,
            ...(selectedRowId && {
              backgroundColor: row.original.id === selectedRowId && '#436c9a',
            }),
            ...(selectedRowIds.length > 0 && {
              backgroundColor: selectedRowIds.includes(row.original.id) && '#436c9a',
            }),
            ...(disabled && {
              cursor: 'not-allowed',
            }),
            ...(!disabled &&
              onRowClick && {
                cursor: 'pointer',
              }),
          },
        };
      }}
      // Dropdown menu styles
      muiDetailPanelProps={() => ({
        sx: {
          backgroundColor: '#436C9A',
          color: 'white',
          ...customStyles.dropdown,
        },
      })}
      renderDetailPanel={({ row }) => (
        // The onClick is to prevent dropdown from collapsing when clicked
        <div className="w-100" onClick={(e) => e.stopPropagation()}>
          {dropdownMenu(row.original)}
        </div>
      )}
      renderEmptyRowsFallback={() => (
        <div className="text-center text-white p-4 fw-bold">No records to display</div>
      )}
    />
  );
};

export default BaseTable;
